import TollAccountType from '../../types/tollAccountType';
import React from 'react';
import {displayableTollAccountType, displayableTollProvider, StateSetter} from '../../utils/helpers';
import {
    Input,
    FormGroup,
    FormRadioGroup,
    Row,
    Col,
} from '@snsw-gel/react';
import TollProvider from '../../types/TollProvider';
import {InputValueAndError} from '../../types/InputValueAndError';
import TollReliefConfig from '../../config/TollReliefConfig';
import {InputOnChangeEvent} from '../../types/kiama';

export interface TollAccountDetailsProps {
    readonly tollAccountType: TollAccountType;
    readonly tollProvider: InputValueAndError<TollProvider>;
    readonly setTollProvider: StateSetter<InputValueAndError<TollProvider>>;
    readonly tollAccountNumber: InputValueAndError<string>;
    readonly setTollAccountNumber: StateSetter<InputValueAndError<string>>;
    readonly licensePlateNumber: InputValueAndError<string>;
    readonly setLicensePlateNumber: StateSetter<InputValueAndError<string>>;
    readonly tollEmail: InputValueAndError<string>;
    readonly setTollEmail: StateSetter<InputValueAndError<string>>;
}

/**
 * A component for the toll account details form.
 * To be used by both personal and sole trader toll account details pages.
 */
const TollAccountDetails: React.FC<TollAccountDetailsProps> = (
    {
        tollAccountType,
        tollProvider,
        setTollProvider,
        tollAccountNumber,
        setTollAccountNumber,
        licensePlateNumber,
        setLicensePlateNumber,
        tollEmail,
        setTollEmail,
    },
) => {
    const readableTollAccType = displayableTollAccountType(tollAccountType);

    return <>
        <p>All fields to be completed unless marked (optional)</p>

        <h2>{readableTollAccType} toll account details</h2>

        {tollAccountType === TollAccountType.Personal && <>
            <h4>Important:</h4>
            <ul>
                <li>
                    You can only link <strong>one</strong> personal toll account.
                </li>
                <li>If you have multiple personal toll accounts, choose the one you use most often.</li>
            </ul>
        </>}

        {tollAccountType !== TollAccountType.Personal && <>
            <h4>Note:</h4>
            <ul>
                <li>You can only link one business toll account at a time.</li>
                <li>All business toll account claims will count towards your business rebate total.</li>
                <li>You cannot combine toll spend between toll accounts.</li>
            </ul>
        </>}

        <FormRadioGroup
            id={TollReliefConfig.anchors.tollProvider}
            name='tollProvider'
            legend='Toll provider'
            helpMessage='Your toll provider is on your toll statement.'
            hasError={tollProvider.hasError}
            errorMessage={tollProvider.errorMessage ?? 'Toll provider is required.'}
            options={[
                {value: TollProvider.EToll, label: displayableTollProvider(TollProvider.EToll)},
                {value: TollProvider.Linkt, label: displayableTollProvider(TollProvider.Linkt)},
            ]}
            onChange={(value: TollProvider) => {
                setTollProvider(tollProvider.withValue(value).validated());
            }}
            value={tollProvider.value}
            vertical
        />

        <Row>
            <Col span={6}>
                <FormGroup
                    id={TollReliefConfig.anchors.tollAccountNumber}
                    label='Toll account number'
                    helpMessage='Your toll account number is on your toll statement.'
                    hasError={tollAccountNumber.hasError}
                    errorMessage={tollAccountNumber.errorMessage ?? 'No toll account number was entered. ' +
                        'Enter up to 25 characters using numbers and letters only.'}
                >
                    <Input
                        name='toll-account-number'
                        onChange={(event: InputOnChangeEvent) => {
                            setTollAccountNumber(tollAccountNumber.withValue(event.target.value.trim()));
                        }}
                        onBlur={() => {
                            setTollAccountNumber(tollAccountNumber.validated());
                        }}
                        value={tollAccountNumber.value}
                        maxLength='25'
                        required
                    />
                </FormGroup>
            </Col>
        </Row>

        <Row>
            <Col span={6}>
                <FormGroup
                    id={TollReliefConfig.anchors.licensePlateNumber}
                    label='Licence plate number'
                    helpMessage='A licence plate number associated with this toll account.'
                    hasError={licensePlateNumber.hasError}
                    errorMessage={licensePlateNumber.errorMessage ?? 'No licence plate was entered. ' +
                        'Enter up to 10 characters using numbers and letters only.'}
                >
                    <Input
                        name='license-plate-number'
                        onChange={(event: InputOnChangeEvent) => {
                            setLicensePlateNumber(licensePlateNumber.withValue(event.target.value.trim()));
                        }}
                        onBlur={() => {
                            setLicensePlateNumber(licensePlateNumber.validated());
                        }}
                        value={licensePlateNumber.value}
                        maxLength='10'
                        required
                    />
                </FormGroup>
            </Col>
        </Row>

        {tollProvider.hasValue() &&
            <Row>
                <Col span={6}>
                    <FormGroup
                        id={TollReliefConfig.anchors.mainEmail}
                        label={`Main email used for your ${displayableTollProvider(tollProvider.value)} account`}
                        helpMessage='This is the primary email listed with your toll account provider.'
                        hasError={tollEmail.hasError}
                        errorMessage={tollEmail.errorMessage ?? 'Main email used for your toll account is required.'}
                    >
                        <Input
                            name='provider-registered-email'
                            onChange={(event: InputOnChangeEvent) => {
                                setTollEmail(tollEmail.withValue(event.target.value.trim()));
                            }}
                            onBlur={() => {
                                setTollEmail(tollEmail.validated())
                            }}
                            value={tollEmail.value}
                            //https://www.rfc-editor.org/rfc/inline-errata/rfc3696.html
                            maxLength='254'
                            required
                        />
                    </FormGroup>
                </Col>
            </Row>
        }
    </>;
};

export default TollAccountDetails;
